import angular from 'angular'

import { noop, wrap } from 'lodash-es'

import fixPost from '../utils/fix-post'
import postFlag from '../utils/post-flag'
import postPosition from '../utils/post-position'
import postModelIndex from '../utils/post-model-index'
import generateModulus from '../utils/generate-modulus'

import {
  RANDOM_ORDER,
  RECENT_ORDER,
  POPULAR_ORDER
} from '../utils/shared-constants'

angular
  .module('ggs.gallery')
  .factory('OrderTypeManager', OrderTypeManagerProvider)

function OrderTypeManagerProvider($q, waitFor, DefineMethod) {

  return function OrderTypeManager(
    properties,
    rpcGetTopPosts,
    extFlow,
    postListManager,
    stayInPostQueue,
    stayInListQueue,
    isAllModalsClosed) {

    const sign = 'galleryMain::orderTypeManager'
    const wrapM = DefineMethod(sign)

    const orderOptions = [{
      desc: properties.key2get.sort_text_popular,
      value: POPULAR_ORDER
    }, {
      desc: properties.key2get.sort_text_recent,
      value: RECENT_ORDER
    }, {
      desc: properties.key2get.sort_text_random,
      value: RANDOM_ORDER
    }]

    const init = wrapM('init', noop)

    const destroy = wrapM('destroy', noop)

    const changeOrderType = wrapM('changeOrderType', (orderOption) => {
      if (orderOption.value === RANDOM_ORDER) {
        return changeOrderTypeToRandom()
      } else if (orderOption.value === RECENT_ORDER) {
        return changeOrderTypeToRecent()
      } else if (orderOption.value === POPULAR_ORDER) {
        return changeOrderTypeToPopular()
      }
    })

    const changeOrderTypeToRandom = wrapM('changeOrderTypeToRandom', () => {
      return gentlyChangeOrderType('changeOrderTypeToRandom', RANDOM_ORDER)
    })

    const changeOrderTypeToRecent = wrapM('changeOrderTypeToRecent', () => {
      return gentlyChangeOrderType('changeOrderTypeToRecent',  RECENT_ORDER)
    })

    const changeOrderTypeToPopular = wrapM('changeOrderTypeToPopular', () => {
      return gentlyChangeOrderType('changeOrderTypeToPopular', POPULAR_ORDER)
    })

    const gentlyChangeOrderType = wrapM('gentlyChangeOrderType',
      (callerName, newOrderType) => {
        if (properties.order_type === newOrderType) {
          return
        }

        // order cannot be changed when gallery-modal-list is open
        // and we should wait until it's idle before perform the check
        return stayInListQueue.ifIdle(() => {
          // cancel pending chunks
          postListManager.rejectPendingChunks()
          // wait on exclusive access to postList
          return stayInPostQueue(() => {
            return promptChangeOrderType(callerName, newOrderType)
          })
        }, (busyErr) => {
          // it's not a handler - error should NOT be halted!
          return $q.reject(busyErr)
        })
      })

    const promptChangeOrderType = wrapM('promptChangeOrderType',
      (callerName, newOrderType) => {
        const fallbackOrderType = properties.order_type

        return isAllModalsClosed().then((closed) => {
          if (!closed) {
            return $q.reject(new Error(`${sign}#${callerName} - some modal not closed!`))
          }

          postListManager.updateRevision()
          postListManager.delDeniedFromPostList()
          postListManager.delUnprocessedFromPostList()
          properties.override('order_type', newOrderType)
          properties.override('modulus', generateModulus(properties.modulus))

          const topPostsMax = postListManager.getMaxId()
          const topPostsLimit = postListManager.getDynamicTopLimit()
          return rpcGetTopPosts(topPostsMax, topPostsLimit)
            .then(postListManager.cutPostsByTopLimit)
            .then(postListManager.assignActualRevision)
            .then((topPosts) => {
              const newPosts = postListManager.filterNewPostsByPostList(topPosts)
              const outPosts = postListManager.filterOutPostsByPostList(topPosts)
              const dupPosts = postListManager.filterDupPostsByPostList(topPosts)

              postModelIndex.preIndex(topPosts)

              fixPost(dupPosts)
              postPosition.below(dupPosts)
              postModelIndex.index(dupPosts)
              postListManager.renewPostList(dupPosts)

              return $q.resolve()
                .then(extFlow.instantMode)
                .then(wrap(newPosts, postListManager.appendPostList))
                .then(wrap(newPosts, waitFirstPosts))
                .then(wrap(outPosts, extFlow.remove))
                .then(extFlow.arrange)
                .then(wrap(newPosts, waitAllPosts))
                .then(postListManager.cleanupPostList)
                .then(postListManager.resetAboveFetchLoop)
                .then(extFlow.lazyMode)
            })
        }).catch((err) => {
          properties.override('order_type', fallbackOrderType)
          return $q.reject(err)
        })
      })

    const waitFirstPosts = wrapM('waitFirstPosts',
      (posts) => {
        const minComfortSize = Math.min(16, posts.length)
        const naiveFirstSize = Math.floor(posts.length / 3)
        const finalFirstSize = Math.max(naiveFirstSize, minComfortSize)
        return waitFor(
          () => posts.filter(postFlag.isProcessed).length,
          (it) => it >= finalFirstSize)
      })

    const waitAllPosts = wrapM('waitAllPosts',
      (posts) =>
        waitFor(
          () => posts.every(postFlag.isProcessed),
          (it) => it === true))

    return {
      init,
      destroy,
      orderOptions,
      changeOrderType
    }
  }
}
